package co.maytes.standupbot.dashboard.features.analytics

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.svg.Svg
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgCircle
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgDefs
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgGroup
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgLine
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgLinearGradient
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgPath
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgPolygon
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgPolyline
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgRect
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgStop
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgText
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCreditCard
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.shared.dto.analytics.CostsDto
import co.maytes.standupbot.shared.dto.analytics.MonthRollup
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.css.px
import kotlin.math.max

private const val CHART_WIDTH = 800
private const val CHART_HEIGHT = 260
private const val PAD_LEFT = 48
private const val PAD_RIGHT = 16
private const val PAD_TOP = 16
private const val PAD_BOTTOM = 32

@Composable
fun CostChart(dto: CostsDto) {
    val months = dto.months.sortedBy { it.month }
    val series = buildCostSeries(months)
    val isEmpty = months.isEmpty() || series.all { s -> s.values.all { it == 0L } }

    val legend = series.map { LegendItem(it.label, it.color) }

    ChartShell(
        eyebrow = "AI SPEND",
        title = "Cost per month",
        subtitle = "Stacked by standup — lower is cheaper. Hover a point for the exact value.",
        isEmpty = isEmpty,
        legend = legend,
        emptyTitle = "No spend to chart yet",
        emptyMessage = "AI cost lands here once standups run. Each post records its exact token spend.",
        emptyIcon = { LucideCreditCard(size = 26) },
    ) {
        var tooltip by remember { mutableStateOf<TooltipHover?>(null) }
        val drawn = rememberChartIntroPlayed()

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier { property("position", "relative") },
        ) {
            Svg(attrs = {
                attr("viewBox", "0 0 $CHART_WIDTH $CHART_HEIGHT")
                attr("width", "100%")
                attr("height", "${CHART_HEIGHT}px")
                attr("role", "img")
                attr("aria-label", "AI cost by month, stacked by team")
            }) {
                renderGrid(maxStack = series.stackMax())

                val stepX = computeStepX(months.size)
                val maxY = series.stackMax().coerceAtLeast(1)

                val stacked = series.stacked()
                stacked.forEachIndexed { seriesIdx, stack ->
                    val points = months.mapIndexed { i, _ ->
                        val x = PAD_LEFT + (i * stepX).toInt()
                        val y = yFor(stack[i], maxY)
                        x to y
                    }
                    val upperBaseline = if (seriesIdx == 0) {
                        months.indices.map { PAD_LEFT + (it * stepX).toInt() to CHART_HEIGHT - PAD_BOTTOM }
                    } else {
                        val below = stacked[seriesIdx - 1]
                        months.mapIndexed { i, _ -> PAD_LEFT + (i * stepX).toInt() to yFor(below[i], maxY) }
                    }
                    val fill = series[seriesIdx].color.withAlpha(0.14)
                    SvgPolygon(attrs = {
                        attr("points", (points + upperBaseline.reversed()).toPointsAttr())
                        attr("fill", fill)
                        attr("stroke", "none")
                        style {
                            property("opacity", if (drawn) "1" else "0")
                            property("transition", ChartMotion.intro("opacity", seriesIdx))
                        }
                    })
                    SvgPolyline(attrs = {
                        attr("points", points.toPointsAttr())
                        attr("fill", "none")
                        attr("stroke", series[seriesIdx].color)
                        attr("stroke-width", "2")
                        attr("stroke-linecap", "round")
                        attr("stroke-linejoin", "round")
                        val length = estimatedLength(points)
                        style {
                            property("stroke-dasharray", "$length")
                            property("stroke-dashoffset", if (drawn) "0" else "$length")
                            property("transition", ChartMotion.intro("stroke-dashoffset", seriesIdx))
                        }
                    })
                    points.forEachIndexed { i, (x, y) ->
                        val monthLabel = formatMonthLabel(months[i].month)
                        val valueCents = series[seriesIdx].values[i]
                        SvgCircle(attrs = {
                            attr("cx", "$x")
                            attr("cy", "$y")
                            attr("r", "3.5")
                            attr("fill", series[seriesIdx].color)
                            attr("stroke", Palette.surface.cssRgb())
                            attr("stroke-width", "2")
                            style {
                                property("opacity", if (drawn) "1" else "0")
                                property("transition", ChartMotion.intro("opacity", i))
                                property("cursor", "pointer")
                            }
                            onMouseEnter {
                                tooltip = TooltipHover(
                                    x = x,
                                    y = y,
                                    content = TooltipContent(
                                        title = monthLabel,
                                        subtitle = months[i].standupCount.let { "$it standups" },
                                        rows = series.mapIndexed { idx, s ->
                                            TooltipContent.Row(
                                                label = s.label,
                                                value = formatCurrency(s.values[i], dto.currency),
                                                color = s.color,
                                            )
                                        },
                                    ),
                                )
                            }
                            onMouseLeave { tooltip = tooltip?.copy(content = null) }
                        })
                    }
                }

                months.forEachIndexed { i, m ->
                    val x = PAD_LEFT + (i * stepX).toInt()
                    SvgText(
                        text = formatMonthLabel(m.month),
                        attrs = {
                            attr("x", "$x")
                            attr("y", "${CHART_HEIGHT - PAD_BOTTOM / 3}")
                            attr("text-anchor", "middle")
                            attr("font-size", "11")
                            attr("font-weight", "500")
                            attr("fill", Palette.textTertiary.cssRgb())
                            attr("font-family", "Inter, sans-serif")
                        },
                    )
                }
            }

            val hovered = tooltip
            if (hovered != null) {
                ChartTooltip(
                    content = hovered.content,
                    x = hovered.x,
                    y = hovered.y,
                    visible = hovered.content != null,
                )
            }
        }
    }
}

private data class TooltipHover(val x: Int, val y: Int, val content: TooltipContent?)

private data class CostSeries(
    val label: String,
    val color: String,
    val values: List<Long>,
)

private fun buildCostSeries(months: List<MonthRollup>): List<CostSeries> {
    if (months.isEmpty()) return emptyList()
    val teamIds = months.flatMap { it.byTeam }.map { it.standupId }.distinct()
    if (teamIds.isEmpty()) {
        return listOf(
            CostSeries(
                label = "Total",
                color = ChartPalette.at(0),
                values = months.map { it.totalCents },
            ),
        )
    }
    val teamNameById = months.flatMap { it.byTeam }.associate { it.standupId to it.name }
    return teamIds.mapIndexed { idx, standupId ->
        CostSeries(
            label = teamNameById[standupId] ?: "Team",
            color = ChartPalette.at(idx),
            values = months.map { m -> m.byTeam.firstOrNull { it.standupId == standupId }?.totalCents ?: 0L },
        )
    }
}

private fun List<CostSeries>.stackMax(): Long {
    if (isEmpty()) return 0L
    val size = first().values.size
    var mx = 0L
    for (i in 0 until size) {
        var sum = 0L
        for (s in this) sum += s.values[i]
        mx = max(mx, sum)
    }
    return mx
}

private fun List<CostSeries>.stacked(): List<List<Long>> {
    if (isEmpty()) return emptyList()
    val size = first().values.size
    val running = LongArray(size)
    return map { s ->
        for (i in 0 until size) running[i] += s.values[i]
        running.toList()
    }
}

private fun yFor(stack: Long, maxY: Long): Int {
    val area = (CHART_HEIGHT - PAD_TOP - PAD_BOTTOM).toDouble()
    val ratio = stack.toDouble() / maxY
    return (CHART_HEIGHT - PAD_BOTTOM - ratio * area).toInt()
}

private fun computeStepX(n: Int): Double {
    if (n <= 1) return 0.0
    val width = (CHART_WIDTH - PAD_LEFT - PAD_RIGHT).toDouble()
    return width / (n - 1)
}

private fun List<Pair<Int, Int>>.toPointsAttr(): String =
    joinToString(" ") { (x, y) -> "$x,$y" }

private fun estimatedLength(points: List<Pair<Int, Int>>): Int {
    var len = 0.0
    for (i in 1 until points.size) {
        val dx = (points[i].first - points[i - 1].first).toDouble()
        val dy = (points[i].second - points[i - 1].second).toDouble()
        len += kotlin.math.sqrt(dx * dx + dy * dy)
    }
    return len.toInt().coerceAtLeast(1)
}

private fun String.withAlpha(alpha: Double): String {
    val clean = trim()
    if (clean.startsWith("rgba")) return clean
    if (!clean.startsWith("rgb(")) return clean
    val inner = clean.removePrefix("rgb(").removeSuffix(")").trim()
    return "rgba($inner, $alpha)"
}

@Composable
private fun renderGrid(maxStack: Long) {
    val yAxisRaw = CHART_HEIGHT - PAD_BOTTOM
    val yAxisTop = PAD_TOP
    val steps = 4
    SvgRect(attrs = {
        attr("x", "$PAD_LEFT")
        attr("y", "$yAxisTop")
        attr("width", "${CHART_WIDTH - PAD_LEFT - PAD_RIGHT}")
        attr("height", "${yAxisRaw - yAxisTop}")
        attr("fill", Palette.surfaceSubtle.cssRgb())
        attr("opacity", "0.5")
        attr("rx", "6")
    })
    for (i in 0..steps) {
        val y = yAxisRaw - (i * (yAxisRaw - yAxisTop) / steps)
        SvgLine(attrs = {
            attr("x1", "$PAD_LEFT")
            attr("x2", "${CHART_WIDTH - PAD_RIGHT}")
            attr("y1", "$y")
            attr("y2", "$y")
            attr("stroke", Palette.border.cssRgb())
            attr("stroke-width", if (i == 0) "1" else "1")
            attr("opacity", if (i == 0) "0.9" else "0.45")
            attr("stroke-dasharray", if (i == 0) "0" else "1 5")
            attr("stroke-linecap", "round")
        })
        val value = (maxStack * i / steps)
        SvgText(
            text = formatCurrency(value),
            attrs = {
                attr("x", "${PAD_LEFT - 12}")
                attr("y", "${y + 4}")
                attr("text-anchor", "end")
                attr("font-size", "11")
                attr("font-weight", "500")
                attr("fill", Palette.textTertiary.cssRgb())
                attr("font-family", "Inter, sans-serif")
                attr("style", "font-variant-numeric: tabular-nums")
            },
        )
    }
}

@Suppress("unused")
private fun referencePxImport() = 1.px
